/**
 * /app/vendor/onboard — vendor onboarding wizard.
 *
 * A focused form for a vendor-admin (e.g. Versatile) to onboard a new foundry:
 * name (+ auto-slug) + first-admin email + optional sand-plant provisioning →
 * POST /v1/vendor/customers. The backend creates an INDEPENDENT child org under
 * the vendor, stamps the persistent co-brand origin, and mints a passwordless
 * admin invite; on success we surface the invite link to share. Gated +
 * self-guarded on useOrg().isVendorAdmin. See
 * docs/versatile/specs/tenancy-vendor-portal.md §4.6.
 */

import { useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Copy, Loader2 } from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Card } from "@/lib/ui/card";
import { useOrg } from "@/lib/org-context";
import { ApiClientError } from "@/lib/api-client";
import { vendor, type CreateCustomerResult } from "@/lib/api/vendor";

function LabelledField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

// Client-side slug preview (mirrors orgs.Slugify; the backend re-derives + owns
// uniqueness — this is a friendly preview / optional override).
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]+/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function createErrorMessage(e: unknown): string {
  if (e instanceof ApiClientError) {
    const code = (e.body as { error?: string } | null)?.error;
    switch (code) {
      case "org_slug_taken":
        return "That slug is already taken — try a different name or slug.";
      case "missing_required_field":
        return "Name and admin email are both required.";
      case "invalid_slug":
        return "Couldn't derive a valid slug — set one explicitly.";
      case "not_vendor_admin":
        return "You don't have vendor-admin access.";
      default:
        return code
          ? `Couldn't onboard the customer (${code}).`
          : "Couldn't onboard the customer.";
    }
  }
  return "Couldn't onboard the customer. Check your connection and try again.";
}

export function VendorOnboard() {
  const { isVendorAdmin } = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [email, setEmail] = useState("");
  const [provision, setProvision] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      vendor.createCustomer({
        name: name.trim(),
        slug: (slugTouched ? slug : slugify(name)) || undefined,
        admin_email: email.trim(),
        provision_sand_plant: provision,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["vendor", "customers"] });
    },
  });

  // Self-guard (defence in depth; the nav + route are already vendor-gated).
  if (!isVendorAdmin) return <Navigate to="/app" replace />;

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const nameOk = name.trim().length > 0;
  const emailOk = EMAIL_RE.test(email.trim());
  const canSubmit = nameOk && emailOk;
  const result = mutation.data;

  return (
    <PageContainer className="max-w-2xl">
      <PageHeader
        breadcrumb={
          <>
            <Link to="/app" className="hover:text-on-surface">
              App
            </Link>
            <span aria-hidden>/</span>
            <Link to="/app/vendor" className="hover:text-on-surface">
              Customers
            </Link>
            <span aria-hidden>/</span>
            <span className="text-on-surface">Onboard</span>
          </>
        }
        title="Onboard a foundry"
        description="Create a new customer org — they get a branded login and you keep oversight of their equipment."
      />

      {result ? (
        <SuccessPanel result={result} onDone={() => navigate("/app/vendor")} />
      ) : (
        <Card className="flex flex-col gap-5 p-6">
          <LabelledField label="Foundry name">
            <Input
              autoFocus
              placeholder="e.g. Acme Iron Works"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </LabelledField>

          <LabelledField label="Slug">
            <Input
              placeholder="acme-iron-works"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugify(e.target.value));
              }}
            />
            <span className="text-[11px] text-on-surface-muted">
              Used in URLs. Auto-derived from the name; the server ensures it's unique.
            </span>
          </LabelledField>

          <LabelledField label="Admin email">
            <Input
              type="email"
              placeholder="owner@acme-iron.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <span className="text-[11px] text-on-surface-muted">
              We'll email them a passwordless invite to set up their login.
            </span>
          </LabelledField>

          <label className="flex items-start gap-2.5">
            <input
              type="checkbox"
              checked={provision}
              onChange={(e) => setProvision(e.target.checked)}
              className="mt-0.5 size-4 rounded border-border accent-brand"
            />
            <span className="text-sm text-on-surface">
              Provision a sand-testing plant
              <span className="block text-[11px] text-on-surface-muted">
                Optional — sets up a starter sand-lab dashboard for them (they can also do this
                themselves later).
              </span>
            </span>
          </label>

          {mutation.isError && (
            <div className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {createErrorMessage(mutation.error)}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-border pt-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/vendor")}>
              <ArrowLeft className="mr-1.5 size-4" /> Cancel
            </Button>
            <Button size="sm" disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 size-4 animate-spin" /> Onboarding…
                </>
              ) : (
                "Onboard customer"
              )}
            </Button>
          </div>
        </Card>
      )}
    </PageContainer>
  );
}

function SuccessPanel({
  result,
  onDone,
}: {
  result: CreateCustomerResult;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is visible to copy manually */
    }
  };

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-2">
        <span className="flex size-7 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="size-4" />
        </span>
        <span className="text-sm font-semibold text-on-surface">
          {result.org.name} onboarded
        </span>
      </div>
      <p className="text-sm text-on-surface-muted">
        Send this invite link to{" "}
        <span className="font-medium text-on-surface">{result.invite.email}</span> — it lets
        them set up their admin login (no password needed from you).
      </p>
      <div className="flex items-center gap-2">
        <Input readOnly value={result.link} className="font-mono text-xs" />
        <Button size="sm" variant="secondary" onClick={copy}>
          {copied ? (
            <>
              <Check className="mr-1.5 size-4" />
              Copied
            </>
          ) : (
            <>
              <Copy className="mr-1.5 size-4" />
              Copy
            </>
          )}
        </Button>
      </div>
      {result.provision_sand_plant_requested && !result.sand_plant_provisioned && (
        <p className="text-[11px] text-on-surface-muted">
          Sand-plant provisioning was requested — the foundry can install it from their dashboard.
        </p>
      )}
      <div className="border-t border-border pt-4">
        <Button size="sm" onClick={onDone}>
          Back to customers
        </Button>
      </div>
    </Card>
  );
}
